import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useMemo } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useQuery } from "convex/react";
import { useReviewTime } from "@/hooks/useReviewTime";
import { api } from "@convex/_generated/api";

export const CardsStudiedChart = () => {
  const reviewTime = useReviewTime();
  const reviewHistory = useQuery(api.reviews.getReviewHistory, { reviewTime });

  // Create a full 30-day dataset starting from the first entry
  // TODO: this was generated by AI - check for bugs.
  const fullDataset = useMemo(() => {
    if (!reviewHistory || reviewHistory.length === 0) return null;

    // Get the earliest date from the review history
    const sortedHistory = [...reviewHistory].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const firstEntry = sortedHistory[0];
    if (!firstEntry) return [];

    const firstEntryDate = new Date(firstEntry.date);

    // Create a map of the existing data for quick lookup
    const existingDataMap = new Map(
      reviewHistory.map((entry) => [new Date(entry.date).toDateString(), entry])
    );

    // Generate a full dataset with entries for 30 days starting from the first entry
    const fullData = [];
    for (let i = 0; i < 30; i++) {
      const currentDate = new Date(firstEntryDate);
      currentDate.setDate(currentDate.getDate() + i);
      const dateString = currentDate.toDateString();

      // Use existing data if available, otherwise create an empty entry
      const existingEntry = existingDataMap.get(dateString);
      if (existingEntry) {
        fullData.push(existingEntry);
      } else {
        fullData.push({
          date: dateString,
          reviews: undefined,
        });
      }
    }

    return fullData;
  }, [reviewHistory]);

  return (
    <Card>
      <CardContent className="px-2 sm:p-6">
        {fullDataset ? (
          <ChartContainer
            config={{
              reviews: {
                label: "Cards Studied",
                color: "var(--chart-4)",
              },
            }}
            className="aspect-auto h-[250px] w-full"
          >
            <BarChart
              accessibilityLayer
              data={fullDataset}
              margin={{
                left: 12,
                right: 12,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: string) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                dataKey="reviews"
                orientation="right"
                allowDecimals={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    className="w-[150px]"
                    nameKey="reviews"
                    labelFormatter={(value: string) => {
                      return new Date(value).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      });
                    }}
                  />
                }
              />
              <Bar dataKey="reviews" fill={`var(--color-reviews)`} />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            You haven't reviewed any cards yet
          </div>
        )}
      </CardContent>
    </Card>
  );
};
